import "../../Overview.css";

import { Dispatch, SetStateAction } from "react";

import { BasicDetailsData, LocationData, SelectData } from "../../../../../../../models";
import { GetProjectDetailsResponse } from "../../../../../../../service/query";
import { Autocomplete, Column, ReadonlyText, Row, Select, TextInput } from "../../../../../../../widget/index";
import { useBasicDetailsSection } from "../BasicDetailsSection/useBasicDetailsSection";
import { useEditableBasic } from "./useEditableBasic";

interface BasicProps {
  projectDetails: GetProjectDetailsResponse;
  basicDetailsData: BasicDetailsData;
  setBasicDetailsData: Dispatch<SetStateAction<BasicDetailsData>>;
  locationData: LocationData;
  setLocationData: Dispatch<SetStateAction<LocationData>>;
  validatorData: SelectData;
  isNoStandard?: boolean;
}

export const EditableBasic = ({
  projectDetails,
  basicDetailsData,
  setBasicDetailsData,
  locationData,
  setLocationData,
  validatorData,
  isNoStandard = false,
}: BasicProps): JSX.Element => {
  const { countries } = useEditableBasic();
  useBasicDetailsSection(projectDetails);

  return (
    <>
      <Row spacingV="ll">
        <Column span={4}>
          <TextInput
            label="Display name"
            name="Display name"
            type="text"
            value={basicDetailsData.displayName}
            onChange={(displayNameNewValue) =>
              setBasicDetailsData({
                ...basicDetailsData,
                displayName: displayNameNewValue,
              })
            }
          />
        </Column>
        <Column span={4} offset={1} />
      </Row>
      {/* REQ-013: Registry name / ID are hidden for No standard projects. */}
      {!isNoStandard && (
        <Row spacingV="ll">
          <Column span={4}>
            <TextInput
              label="Registry name"
              name="Registry name"
              type="text"
              value={basicDetailsData.name}
              onChange={(nameNewValue) =>
                setBasicDetailsData({
                  ...basicDetailsData,
                  name: nameNewValue,
                })
              }
            />
          </Column>
          <Column span={4} offset={1}>
            <TextInput
              label="Registry ID"
              name="Registry ID"
              type="text"
              value={basicDetailsData.registryId}
              onChange={(registryIdNewValue) =>
                setBasicDetailsData({
                  ...basicDetailsData,
                  registryId: registryIdNewValue,
                })
              }
            />
          </Column>
        </Row>
      )}
      <Row spacingV="ll">
        <Column span={4}>
          <ReadonlyText label="Project type" value={basicDetailsData.projectType} />
        </Column>
        <Column span={4} offset={1}>
          <TextInput
            label="Total area"
            name="Total area"
            type="number"
            value={basicDetailsData.totalArea}
            onChange={(totalAreaNewValue) =>
              setBasicDetailsData({
                ...basicDetailsData,
                totalArea: totalAreaNewValue,
              })
            }
          />
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={4}>
          <TextInput
            label="Grid reference"
            name="Grid reference"
            value={locationData.gridReference}
            onChange={(gridReferenceNewValue) =>
              setLocationData({
                ...locationData,
                gridReference: gridReferenceNewValue,
              })
            }
          />
        </Column>
        <Column span={4} offset={1}>
          <TextInput
            label="County / Region / State"
            name="Grid reference"
            value={locationData.addressRegion}
            onChange={(addressRegionNewValue) =>
              setLocationData({
                ...locationData,
                addressRegion: addressRegionNewValue,
              })
            }
          />
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={4}>
          {countries && (
            <Autocomplete
              label="Country"
              data={countries}
              initialKey={locationData.addressCountryCode}
              onSelect={(addressCountryCodeNewValue) =>
                setLocationData({
                  ...locationData,
                  addressCountryCode: addressCountryCodeNewValue,
                })
              }
            />
          )}
        </Column>
        <Column span={6} offset={1}>
          {validatorData.length ? (
            <Row>
              <Column span={10}>
                <Select
                  label="Validation & verification body (VVB)"
                  data={validatorData}
                  value={
                    validatorData.some((e) => e.key === basicDetailsData.validatorUuid)
                      ? basicDetailsData.validatorUuid
                      : undefined
                  }
                  onChange={(validatorUuidNewValue) =>
                    setBasicDetailsData({
                      ...basicDetailsData,
                      validatorUuid: validatorUuidNewValue,
                    })
                  }
                />
              </Column>
            </Row>
          ) : (
            <>
              <Row spacingV="m">
                <Column span={12}>
                  <h3>Validation & verification body (VVB)</h3>
                </Column>
              </Row>
              <Row>
                <Column span={10}>
                  <p className="body1 NoData">No validators available for this project</p>
                </Column>
              </Row>
            </>
          )}
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={4}>
          <TextInput
            label="Latitude"
            name="Latitude"
            value={locationData.latitude}
            onChange={(value) =>
              setLocationData({
                ...locationData,
                latitude: value,
              })
            }
          />
        </Column>
        <Column span={4} offset={1}>
          <TextInput
            label="Longitude"
            name="Longitude"
            value={locationData.longitude}
            onChange={(value) =>
              setLocationData({
                ...locationData,
                longitude: value,
              })
            }
          />
        </Column>
      </Row>
    </>
  );
};
